import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/flavors", label: "This Week's Flavors" },
  { to: "/order", label: "Order Cookies" },
  { to: "/custom-orders", label: "Custom Orders" },
  { to: "/rewards", label: "Rewards" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const onAuthPage = loc.pathname === "/login" || loc.pathname === "/signup";
  const redirectQs = onAuthPage ? new URLSearchParams(loc.search).get("redirect") : null;
  const loginTo = redirectQs ? `/login?redirect=${encodeURIComponent(redirectQs)}` : "/login";
  const signupTo = redirectQs ? `/signup?redirect=${encodeURIComponent(redirectQs)}` : "/signup";

  useEffect(() => {
    api.get("/announcements").then(r => setAnnouncements(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <div className="min-h-screen">
      {announcements.length > 0 && (
        <div className="bg-white/15 backdrop-blur-sm text-white text-center text-sm py-2 px-4 font-semibold relative z-50" data-testid="announcement-banner">
          {announcements[0].text}
        </div>
      )}

      <header className="fixed left-0 right-0 z-40 top-0" data-testid="site-header">
        <div className="flex items-center justify-end px-5 md:px-8 pt-5 md:pt-7">
          <button
            onClick={() => setOpen(true)}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.5} />
          </button>
        </div>
      </header>

      {/* Slide-down full-width menu (works on all sizes) */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="main-menu"
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
        <div className={`relative max-w-md w-full ml-auto h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <p className="font-display font-extrabold text-lg tracking-tight">Menu</p>
            <button
              onClick={() => setOpen(false)}
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-[#111827] flex items-center justify-center"
              data-testid="nav-menu-close"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3.5 rounded-2xl font-display font-extrabold text-xl uppercase tracking-tight transition-colors ${
                    isActive ? "bg-[#FFE4EE] text-[#FF1F87]" : "text-[#111827] hover:bg-gray-50"
                  }`
                }
                data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            {user && user.role === "customer" && (
              <NavLink
                to="/orders"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3.5 rounded-2xl font-display font-extrabold text-xl uppercase tracking-tight transition-colors ${
                    isActive ? "bg-[#FFE4EE] text-[#FF1F87]" : "text-[#111827] hover:bg-gray-50"
                  }`
                }
                data-testid="nav-link-your-orders"
              >
                Your Orders
              </NavLink>
            )}
          </nav>
          <div className="border-t border-gray-100 px-6 py-5">
            {user ? (
              <div className="space-y-3">
                <Link
                  to={user.role === "admin" ? "/admin" : "/rewards"}
                  onClick={() => setOpen(false)}
                  className="block w-full text-center btn-pink !py-3"
                  data-testid="nav-account-link"
                >
                  {user.role === "admin" ? "ADMIN DASHBOARD" : `MY ACCOUNT`}
                </Link>
                <button
                  onClick={() => { logout(); setOpen(false); nav("/"); }}
                  className="w-full text-sm font-bold text-[#4B5563] flex items-center justify-center gap-2 py-2"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Link to={loginTo} onClick={() => setOpen(false)} className="block w-full text-center btn-outline-pink !py-3" data-testid="nav-login-link">LOG IN</Link>
                <Link to={signupTo} onClick={() => setOpen(false)} className="block w-full text-center btn-pink !py-3" data-testid="nav-signup-link">SIGN UP</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <main>{children}</main>

      <footer className="bg-black/15 backdrop-blur-sm text-white mt-16 border-t border-white/15">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 text-center text-white/85 text-sm">
          <p className="font-display font-extrabold text-xl text-white">Sugary Sweet</p>
          <p className="mt-1">Rock Hill, South Carolina</p>
          <p className="mt-2 text-white/70"><a href="https://sugarysweeteverything.com" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline" data-testid="footer-site-link">sugarysweeteverything.com</a> · &copy; {new Date().getFullYear()} Sugary Sweet</p>
        </div>
      </footer>
    </div>
  );
}
